import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import clsx from "clsx";
import { HttpStatusCode } from "axios";

import { GoodsItemType, GoodsTypeId } from "../../../types/goods";
import { BuildingId } from "../../../types/building";

import { NearBuildingItem } from "../../../services/buildingServices";
import { getGoodsRentalList } from "../../../services/goodsRentalServices";
import { PageableRequest } from "../../../services/talkServices";
import { getBuildingList } from "../../../services/userServiceServices";

import { useUserContext } from "../../../contexts/userContext";

import useSimplePopup from "../../../hooks/useSimplePopup";
import useInfiniteScroll, {
  INFINITE_CODE,
} from "../../../hooks/useInfiniteScroll";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import StickyHeader from "../../../components/Layout/StickyHeader";
import { AuthHeader } from "../../../components/Layout/Auth/Header";
import CommonSelect, { SelectOption } from "../../../components/Select";
import { TalkFindBuildingDownArrow } from "../../Talk";
import CommonCheckbox from "../../../components/Input/Checkbox";
import ServiceRentListItem from "../../../components/Service/Rent/ListItem";
import { TalkSearchInput } from "../../../components/SearchHeader";
import FindBuildingPopup from "../../../components/Popup/FindBuilding";
import { Reason } from "../../../types/location";

type Props = {};

export type ServiceRentalState = {
  parentUrl: string;
  parentState: BuildingId & {
    buildingName: string;
    type: string;
  };
};

export default function ServiceRental(props: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const [user] = useUserContext();
  const typeList: SelectOption[] = [
    {
      value: "UMBRELLA",
      label: "우산",
    },
    {
      value: "TOOLS",
      label: "공구",
    },
    {
      value: "BOOK",
      label: "책",
    },
    { value: "ITEM", label: "물품" },
  ];
  const [selectedType, setSelectedType] = useState<SelectOption>();
  const [isActivityRental, setIsActivityRental] = useState(true);
  const [selectedBuilding, setSelectedBuilding] =
    useState<Partial<NearBuildingItem>>();

  const sortOption: SelectOption[] = [
    { label: "최신 등록순", value: "createdAt.desc" },
  ];
  const [sort, setSort] = useState<SelectOption>(sortOption[0]);
  const [goodsList, setGoodsList] = useState<GoodsItemType[]>([]);
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [isLoading, setIsLoading] = useState(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [searchWord, setSearchWord] = useState("");
  const [popup, setPopup] = useState<"selectBuilding" | "">("");
  const [buildingList, setBuildingList] = useState<SelectOption[]>([]);
  const reason = Reason.BUILDING_CHANGE;

  const loadGoodsList = async ({
    searchWord,
    page = 1,
    pageSize = 10,
  }: { searchWord?: string } & PageableRequest) => {
    // 초기값이 없을때 호출한 데이터가 더 늦게 반환되면서 최신데이터가 엎어지는 문제 개선을 위한 예외 처리
    if (selectedType === undefined) return INFINITE_CODE.ERROR;
    let itemCounts = 0;

    setIsLoading(true);
    try {
      const result = await getGoodsRentalList(
        selectedBuilding?.buildingId ?? user?.buildingId ?? "",
        {
          isActivityRental,
          goodsTypeId: (selectedType?.value ??
            typeList[0].value) as GoodsTypeId,
          goodsName: searchWord,
          page,
          pageSize,
        }
      );
      if (result.status === HttpStatusCode.Ok) {
        const { items } = result.data;

        if (page === 1) {
          setGoodsList(items);
        } else {
          setGoodsList((prev) => [...prev, ...items]);
        }
        itemCounts = items.length;
      } else {
        setGoodsList([]);
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("재고 변경에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
    return itemCounts;
  };

  const [, { resetScroll }] = useInfiniteScroll({
    callback: async (args) => {
      return loadGoodsList({
        ...args,
        searchWord,
      });
    },
    isLoading: isLoading,
  });

  useEffect(() => {
    if (user.isLoading) return;

    if (selectedBuilding === undefined) {
      if (location?.state?.buildingId) {
        setSelectedBuilding({
          buildingId: location.state?.buildingId,
          buildingName: location.state?.buildingName,
        });
      } else {
        setSelectedBuilding({
          buildingId: "ALL",
          buildingName: "전체",
        });
      }
    }
    resetScroll();
    loadGoodsList({
      searchWord,
    });
  }, [
    user.isLoading,
    isActivityRental,
    selectedType,
    searchWord,
    selectedBuilding?.buildingId,
  ]);

  useEffect(() => {
    const loadBuildingList = () => {
      getBuildingList({
        categoryCode: "RENTAL",
        buildingId: user.buildingId ?? "",
      }).then((res) => {
        if (res.status === HttpStatusCode.Ok && res.data) {
          setBuildingList([
            { value: "ALL", label: "전체" },
            ...res.data.map((item: any) => ({
              value: item.buildingId,
              label: item.buildingName,
            })),
          ]);
        }
      });
    };

    if (user.isLoading) return;
    loadBuildingList();
  }, [user.isLoading]);

  useEffect(() => {
    if (location?.state?.type) {
      const type = typeList.find(
        (item) => item.value.toLowerCase() === location.state.type.toLowerCase()
      );
      if (type) {
        setSelectedType(type);
      } else {
        setSelectedType(typeList[0]);
      }
    } else {
      setSelectedType(typeList[0]);
    }
  }, []);

  return (
    <div>
      {loadingComponent}
      <StickyHeader
        title="대여하기"
        onBackClick={() => navigate("/view")}
        className={clsx(["px-5 transition-all text-brand-gray-140 !z-30"])}
      />

      <AuthHeader className="px-5" ruleClassName="h-[6px]">
        <div className="flex flex-col gap-2.5">
          <div className="flex flex-wrap gap-1.5">
            <div className="w-[120px]">
              <CommonSelect
                optionList={typeList}
                defaultValue=" "
                onChange={(value) => setSelectedType(value)}
                value={selectedType?.value}
                arrowIcon={<TalkFindBuildingDownArrow />}
                optionOrder="between"
                variant="selectBuilding"
                unUsePadding
              />
            </div>
            대여 하시겠어요?
          </div>

          <TalkSearchInput onClick={setSearchWord} className="rounded" />
        </div>
      </AuthHeader>

      <div className="flex flex-col gap-2.5">
        <div className="px-5 py-[15px] flex gap-5 border-b border-b-brand-neutral-10 items-center">
          <CommonSelect
            value={selectedBuilding?.buildingId}
            optionList={buildingList}
            onChange={(value) =>
              setSelectedBuilding((prev) => ({
                ...prev,
                buildingId: value.value,
                buildingName: value.label,
              }))
            }
            className="px-[15px] py-2.5 flex gap-2.5 items-center text-sm leading-4 text-brand-neutral-80 border border-brand-primary-gray-100 border-opacity-10 rounded-full w-fit"
            wrapperClassName="w-fit"
            arrowIcon={<ServiceRentalDownArrow />}
            optionWidth="200px"
            optionOrder="between"
          />
          <div className="flex items-center gap-2.5 leading-[19px] design_min:whitespace-nowrap">
            <CommonCheckbox
              checked={isActivityRental}
              onClick={(value) => setIsActivityRental(value)}
            />
            대여 가능만
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="px-5 flex gap-2.5 justify-end">
            <CommonSelect
              value={sort.value}
              optionList={sortOption}
              onChange={(option) => {
                setSort(option);
              }}
              className="border-none w-[140px] text-right"
              wrapperClassName="flex justify-end"
              disabled={sortOption.length <= 1}
              variant="transparent"
            />
          </div>
          <div className="px-5 flex flex-col gap-2.5">
            {goodsList.map((goods) => (
              <ServiceRentListItem
                key={goods.stockId}
                goods={goods}
                state={{
                  parentUrl: "/service/rental",
                  parentState: {
                    buildingId: selectedBuilding?.buildingId ?? "",
                    buildingName: selectedBuilding?.buildingName ?? "",
                    type: selectedType?.value ?? "",
                  },
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <FindBuildingPopup
        popup={popup}
        onClose={() => setPopup("")}
        onChangeBuilding={(building) => {
          setSelectedBuilding({
            buildingId: building.buildingId,
            buildingName: building.buildingName,
          });
        }}
        type={"RENTAL"}
        reason={reason}
      />
      {simplePopupComponent}
    </div>
  );
}

export const ServiceRentalSearchIcon = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20.9999 21.5L15.857 16.3571M10.7142 18.9286C6.45368 18.9286 2.99988 15.4748 2.99988 11.2143C2.99988 6.9538 6.45368 3.5 10.7142 3.5C14.9746 3.5 18.4284 6.9538 18.4284 11.2143C18.4284 15.4748 14.9746 18.9286 10.7142 18.9286Z"
      stroke="#1D1929"
      strokeWidth="1.75"
    />
  </svg>
);

export const ServiceRentalDownArrow = () => (
  <svg
    width="11"
    height="8"
    viewBox="0 0 11 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.3 7.43333C5.9 7.96667 5.1 7.96667 4.7 7.43333L0.699999 2.1C0.205572 1.44076 0.675954 0.500001 1.5 0.500001L9.5 0.5C10.324 0.5 10.7944 1.44076 10.3 2.1L6.3 7.43333Z"
      fill="#333F48"
      fillOpacity="0.4"
    />
  </svg>
);
